import { v4 as uuidv4 } from "uuid";
import { InputType } from "../../../utils/enums";

export const getCurrentPathname = () => {
   const pathnameArray = window.location.pathname.split("/");
   return pathnameArray[pathnameArray.length - 1];
};

export const debounce = (func, delay) => {
   let timeoutId;

   return function(...args) {
      if (timeoutId) {
         clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
         func.apply(this, args);
      }, delay);
   };
};

export const answerPayload = (questionId, formData, index, input) => {
   const types = {
      34: "text",
      32: "choices",
      33: "choices",
      35: "number",
      36: "number",
      37: "text",
      38: "text",
      40: "text",
      100: "number",
      104: "text",
      105: "coords",
      106: "answerFiles",
      110: "answerFiles",
      112: "questionChoice",
      113: "text",
      275: "number",
      277: "Matrix",
      278: "Ranking",
      276: "Rating",
      279: "text"
   };

   switch (questionId) {
      case 32:
      case 33:
         for (let i = 0; i < input.answerDetail.choices.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.choices[${i}]`,
               input.answerDetail.choices[i]
            );
         }
         break;
      case 278:
         for (let i = 0; i < input.answerDetail.answerRankings.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.answerRankings[${i}].choiceId`,
               input.answerDetail.answerRankings[i].choiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerRankings[${i}].rank`,
               input.answerDetail.answerRankings[i].rank
            );
         }
         break;
      case 277:
         for (let i = 0; i < input.answerDetail.answerMatrix.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.answerMatrix[${i}].choiceId`,
               input.answerDetail.answerMatrix[i].choiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerMatrix[${i}].secondChoiceId`,
               input.answerDetail.answerMatrix[i].secondChoiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerMatrix[${i}].text`,
               input.answerDetail.answerMatrix[i].text
            );
         }
         break;
      case 276:
         for (let i = 0; i < input.answerDetail.answerRatings.length; i++) {
            formData.append(
               `answers[${index}].answerDetail.answerRatings[${i}].choiceId`,
               input.answerDetail.answerRatings[i].choiceId
            );
            formData.append(
               `answers[${index}].answerDetail.answerRatings[${i}].secondChoiceId`,
               input.answerDetail.answerRatings[i].secondChoiceId
            );
         }
         break;
      case 105: {
         const coords = input.answerDetail.coords;

         formData.append(
            `answers[${index}].answerDetail.answerGeoLocation.Latitude`,
            coords.latitude
         );
         formData.append(
            `answers[${index}].answerDetail.answerGeoLocation.Longitude`,
            coords.longitude
         );

         if (coords.altitude)
            formData.append(
               `answers[${index}].answerDetail.answerGeoLocation.Altitude`,
               coords.altitude
            );

         if (coords.accuracy)
            formData.append(
               `answers[${index}].answerDetail.answerGeoLocation.Accuracy`,
               coords.accuracy
            );
         break;
      }
      case 106: {
         const answerFiles = input.answerDetail.answerFiles;
         formData.append(`answers[${index}].answerDetail.answerFiles`, answerFiles);
         break;
      }
      case 113:
         formData.append(`answers[${index}].answerDetail.text`, input.answerDetail.text);
         break;
      default:
         formData.append(
            `answers[${index}].answerDetail.${types[questionId]}`,
            input.answerDetail[types[questionId]]
         );
         break;
   }
};

/** A rendered element id / input key that names one instance of a repeated item: it ends in "/n". */
const REPEAT_INSTANCE = /\/\d+$/;

/**
 * The id the backend knows an item by. The renderer keys a repeat instance
 * "<survey>/<group>__repeat/<questionId>/<n>" so that every instance is a separate input, but the
 * question itself is still one row - so the instance number comes off again on the way out, and
 * rides along as `repeatGroup` instead.
 */
const baseId = (id) => (REPEAT_INSTANCE.test(id) ? id.split("/").at(-2) : id);

/**
 * How many times a submitted repeat group was repeated, read off the answers it holds: every
 * answer given inside a repeat carries the instance it belongs to, so the highest one is the
 * count. Nested sections are searched too - a group can hold sections as well as questions.
 *
 * The section's own `repeatGroup` cannot answer this. The backend keeps one such number per
 * section and every submission of the survey overwrites it, so it describes whichever submission
 * was saved last and not the one being read.
 */
export const highestRepeatInstance = (items) =>
   (items ?? []).reduce((highest, item) => {
      const inAnswers = (item.surveyItem?.answers ?? []).reduce(
         (instance, answer) => Math.max(instance, answer?.answerDetail?.repeatGroup ?? 0),
         0
      );

      return Math.max(highest, inAnswers, highestRepeatInstance(item.innerItems));
   }, 0);

/**
 * The question half of a PublishSurvey body: the answers, the repeat counters of the sections
 * holding them, and the ids of every item that was on screen.
 *
 * `renderedInputs` are the store's inputs that survived the on-screen check, `elementIds` the
 * `data-id` of every rendered question. Kept out of the component so the shape of what gets
 * submitted - `answers[i].answerDetail.repeatGroup` above all - can be asserted in a test.
 */
export const appendSubmissionAnswers = (formData, renderedInputs, elementIds) => {
   renderedInputs
      .filter((input) => input.questionType !== InputType.SECTION)
      .forEach((input, index) => {
         formData.append(`answers[${index}].surveyId`, input.surveyId);
         formData.append(`answers[${index}].userId`, input.userId);
         formData.append(`answers[${index}].questionId`, baseId(input.questionId));
         formData.append(`answers[${index}].answerDetail.questionType`, input.questionType);

         if (REPEAT_INSTANCE.test(input.questionId)) {
            formData.append(
               `answers[${index}].answerDetail.repeatGroup`,
               input.questionId.split("/").at(-1)
            );
         }

         answerPayload(input.questionType, formData, index, input);
      });

   renderedInputs
      .filter((input) => input.questionType === InputType.SECTION)
      .forEach((input, index) => {
         formData.append(`sectionRepeatGroups[${index}].sectionId`, baseId(input.questionId));
         formData.append(
            `sectionRepeatGroups[${index}].repeatGroup`,
            input.answerDetail.number ?? 0
         );
      });

   elementIds.forEach((elementId, index) => {
      formData.append(`questionIds[${index}]`, baseId(elementId));
   });
};

export function getGeolocation() {
   return new Promise((resolve, reject) => {
      if ("geolocation" in navigator) {
         navigator.geolocation.getCurrentPosition(
            (position) => {
               const geolocation = {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude
               };
               resolve(geolocation);
            },
            (error) => {
               reject(error);
            }
         );
      } else {
         reject("Geolocation is not available in this browser.");
      }
   });
}

/**
 * Whether a level's `order` numbers can be trusted to order it.
 *
 * They can when every item has one and no two share it. `order` is dense, 1-based and shared
 * between a parent's sections and questions, so within one level two items never claim the same
 * position - but a payload that breaks that promise would shuffle the level if sorted on, and the
 * array it arrived in is then the only information there is. This is the same rule `diffSurvey`
 * applies to a question's choices, for the same reason.
 */
const hasUsableOrder = (items) => {
   const orders = items.map((item) => item.surveyItem?.order);

   return (
      orders.every((order) => typeof order === "number") && new Set(orders).size === orders.length
   );
};

/**
 * The items of one level in the order the survey is meant to be read, without touching the array
 * it was given.
 *
 * `order` decides, and the array position is the fallback for a level whose numbers cannot decide
 * (see `hasUsableOrder`). Both halves matter: the read payload lists a level's sections before the
 * questions that precede them, so the array alone puts a question that sits outside a section
 * underneath it - and numbers that repeat across a level would shuffle the level if sorted on.
 *
 * Every renderer of the tree uses this, because the ones that sorted and the ones that read the
 * array straight used to disagree - the form builder showed a question above the section that
 * follows it and the logic builder showed it below.
 */
export const orderedItems = (items) => {
   const list = (items ?? []).filter(Boolean);

   if (!hasUsableOrder(list)) return list;

   return [...list].sort((a, b) => a.surveyItem.order - b.surveyItem.order);
};

/**
 * Rewrites every `order` in the tree from its array position, in place.
 *
 * Used after a structural edit, which puts the item where it belongs in the array and leaves the
 * numbering to this. On a level whose numbers were already normalised - which is every level, once
 * a read has been through `normalizeItemOrder` - array position and `order` say the same thing.
 */
export const renumberItems = (items) => {
   if (!Array.isArray(items)) return items;

   items.forEach((item, index) => {
      if (item?.surveyItem) {
         item.surveyItem.order = index + 1;
      }

      if (Array.isArray(item?.innerItems)) {
         renumberItems(item.innerItems);
      }
   });

   return items;
};

/**
 * Sorts a read payload's tree into its render order and renumbers it 1..n, level by level, in
 * place - so from then on the array and `order` agree and every tab shows the same survey.
 *
 * Without this the two disagreed and each tab picked a side: the payload lists a level's sections
 * ahead of the questions that come before them, `order` is what says otherwise, and only a
 * section's direct children were ever renumbered - never the survey's own level, where a question
 * outside every section lives, and never a section nested in a section.
 *
 * The arrays are sorted in place rather than replaced because the pristine copy the save diffs
 * against is serialised from the same objects.
 */
export const normalizeItemOrder = (items) => {
   if (!Array.isArray(items)) return items;

   items.splice(0, items.length, ...orderedItems(items));

   items.forEach((item, index) => {
      if (item?.surveyItem) {
         item.surveyItem.order = index + 1;
      }

      if (Array.isArray(item?.innerItems)) {
         normalizeItemOrder(item.innerItems);
      }
   });

   return items;
};

/**
 * The tree node with this id, wherever it sits: inside a section, inside a nested section, or at
 * the survey's own level - plus the array it sits in and its position there.
 *
 * Everything that edits one item goes through this rather than walking down to a `sectionId`
 * first. The id an editor hands down is `surveyItem.sectionId`, the field the READ put on the
 * question, and a walk that trusted it dropped the edit whenever that field was not the
 * question's actual parent: a question at the survey's own level has no section at all (an
 * imported XLSForm can have those), and nothing in the editor updates the field. The edit went
 * nowhere, the save reported success, and the wording came back on the next read.
 *
 * @returns {{node: Object, siblings: Array, index: number}|null}
 */
export const findItemById = (items, id) => {
   if (!Array.isArray(items) || !id) return null;

   for (let index = 0; index < items.length; index += 1) {
      const node = items[index];

      if (node?.id === id) {
         return { node, siblings: items, index };
      }

      const found = findItemById(node?.innerItems, id);

      if (found) return found;
   }

   return null;
};

/** Applies `change` to one item's `surveyItem`, or does nothing if the tree has no such item. */
const editItem = (survey, id, change) => {
   const found = findItemById(survey, id);

   if (found?.node?.surveyItem) {
      change(found.node.surveyItem, found.node);
   }

   return survey;
};

/** The array an add belongs in: a section's children, or the survey's own level when none named. */
const childrenOf = (survey, sectionId) => {
   if (!sectionId) return survey;

   return findItemById(survey, sectionId)?.node?.innerItems ?? null;
};

const createQuestion = (question) => {
   const id = uuidv4();
   return {
      itemLabel: "Question",
      id: id,
      surveyItem: {
         title: "",
         name: "",
         description: null,
         questionType: question.questionType,
         order: question.order,
         sectionId: question.sectionId,
         created: true,
         isRequired: false,
         questionLabels: [],
         surveyQuestionHints: [],
         selectQuestionChoices: [],
         range: null,
         secondChoices: [],
         id: id
      }
   };
};

const createInnerSection = (order) => {
   const id = uuidv4();
   return {
      itemLabel: "Section",
      id: id,
      sectionId: id,
      surveyItem: {
         description: "",
         order: order
      },
      innerItems: [],
      created: true
   };
};

// export const addQuestion = (survey, payload) => {
//    const { order, sectionId } = payload;
//    survey.forEach((item) => {
//       if (item.itemLabel === "Section") {
//          if (item.id !== sectionId) {
//             addQuestion(item.innerItems, payload);
//          } else {
//             console.log("dwdw ", item);
//             for (let i = order - 1; i < item.innerItems.length; i++) {
//                item.innerItems[i].surveyItem.order += 1;
//             }
//             item.innerItems.splice(order, 0, createQuestion(payload));
//          }
//       }
//    });
// };
export const addQuestion = (survey, payload) => {
   const { order, sectionId } = payload;
   // No section named means the survey's own level - which is where the "+" beside a question that
   // sits outside every section points, and where it used to add nothing at all.
   const siblings = childrenOf(survey, sectionId);

   if (!siblings) return survey;

   siblings.splice(order - 1, 0, createQuestion(payload));
   renumberItems(siblings);

   return survey;
};

export const addInnerSection = (survey, payload) => {
   const { order, sectionId } = payload;
   const siblings = childrenOf(survey, sectionId);

   if (!siblings) return survey;

   // `order - 1` is the position the new section is meant to occupy, the same convention
   // `addQuestion` uses - the "+" divider passes the order of the item it sits under plus one.
   // Inserting a slot further along (which is what this did) put the section after the following
   // sibling in the array while numbering it before, so the tabs that sort by `order` and the
   // tabs that read the array showed it in two different places.
   siblings.splice(order - 1, 0, createInnerSection(order));
   renumberItems(siblings);

   return survey;
};

/**
 * Where at the survey's own level the item with this id lives - itself, or the section holding it.
 *
 * A new section always joins the survey's own level, so an "add new section" divider under a
 * question inside a section means "after that section". This answers a position rather than an
 * `order`, because the two are not interchangeable in a read payload: sections and questions are
 * numbered in separate sequences, so an `order` used as an index lands on the wrong sibling.
 */
const rootIndexOf = (survey, targetId) =>
   survey.findIndex((item) => item.id === targetId || !!findItemById(item?.innerItems, targetId));

export const addSection = (survey, targetId) => {
   const index = rootIndexOf(survey, targetId);

   if (index === -1) return survey;

   survey.splice(index + 1, 0, createInnerSection(index + 2));
   renumberItems(survey);

   return survey;
};
export const addFirstSection = (survey) => {
   survey.splice(0, 0, createInnerSection(1));
   renumberItems(survey);

   return survey;
};

export const deleteQuestion = (survey, questionId) => {
   const found = findItemById(survey, questionId);

   if (!found) return survey;

   const { siblings, index } = found;

   siblings.splice(index, 1);
   renumberItems(siblings);

   return survey;
};

export const deleteSection = (survey, sectionId) => {
   const found = findItemById(survey, sectionId);

   if (!found || found.node.itemLabel !== "Section") return survey;

   found.siblings.splice(found.index, 1);
   renumberItems(survey);

   return survey;
};

const generateDummyId = () => {
   return (
      "dummy" +
      Math.random()
         .toString(36)
         .substr(2, 9)
   );
};

export const duplicateQuestion = (survey, questionId) => {
   const found = findItemById(survey, questionId);

   if (!found) return survey;

   const { siblings, index, node } = found;

   const id = uuidv4();
   // A copy of a choice is a new choice: its own placeholder id, and no label id, or the save
   // would write the copy's wording onto the original's rows.
   const copyChoices = (choices) =>
      (choices ?? []).map((choice) => ({
         id: generateDummyId(),
         choiceLabels: [{ id: undefined, text: choice?.choiceLabels?.[0]?.text ?? "" }]
      }));

   const questionItems = {
      ...node.surveyItem,
      order: node.surveyItem.order + 1,
      id: id,
      duplicated: true,
      // A question the author has not titled yet has no label row at all, so this reads through
      // rather than off the first one.
      questionLabels: [{ text: node.surveyItem.questionLabels?.[0]?.text ?? "" }],
      selectQuestionChoices: copyChoices(node.surveyItem.selectQuestionChoices),
      secondChoices: copyChoices(node.surveyItem.secondChoices)
   };

   // After the original, in the array as well as in `order`: the copy used to be spliced in
   // front of it, so the tabs that sort by `order` showed the copy second and the tabs that read
   // the array showed it first.
   siblings.splice(index + 1, 0, { ...node, id: id, surveyItem: questionItems });
   renumberItems(siblings);

   return survey;
};

export const updateQuestionHint = (survey, questionId, text) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.surveyQuestionHints = [{ text: text }];
   });

export const updateIsRequiredStatus = (survey, questionId, status) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.isRequired = status;
   });

export const addEmojiStatus = (survey, questionId, emojiStatus) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.hasEmoji = emojiStatus;
   });

export const addImageToSurveyData = (survey, questionId, image) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.image = image;
   });

export const removeImageFromSurveyData = (survey, questionId) =>
   editItem(survey, questionId, (surveyItem) => {
      delete surveyItem.image;
   });

export const updateQuestionLabel = (survey, questionId, text) =>
   editItem(survey, questionId, (surveyItem, node) => {
      // One editor writes both a section's title and a question's wording; which field the text
      // lands in is decided by what the id turned out to name.
      if (node.itemLabel === "Section") {
         surveyItem.description = text;
      } else {
         surveyItem.questionLabels = [{ text: text }];
      }
   });

export const updateQuestionCalculation = (survey, questionId, calculation) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.calculation = calculation;
   });

export const updateQuestionDefault = (survey, questionId, defaultValue) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.default = defaultValue;
   });

export const updateQuestionType = (survey, questionId, type) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.questionType = type;
   });

export const updateQuestionChoices = (survey, questionId, choices) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.selectQuestionChoices = choices;
   });

export const updateMaxChoices = (survey, questionId, maxChoices) =>
   editItem(survey, questionId, (surveyItem) => {
      const constraintValue = surveyItem.constraint?.trim();

      if (/count-selected\(.\)\s*<=\s*([0-9]+)/.test(constraintValue)) {
         let newConstraint = constraintValue.replace(
            /count-selected\(.\)\s*<=\s*([0-9]+)/,
            maxChoices ? `count-selected(.) <= ${maxChoices}` : ""
         );
         newConstraint = newConstraint.trim().startsWith("and")
            ? newConstraint.replace("and", "")
            : newConstraint;
         surveyItem.constraint = newConstraint.trim();
      } else {
         surveyItem.constraint = constraintValue
            ? `count-selected(.) <= ${maxChoices} ${
                 constraintValue.startsWith("and") ? "" : "and"
              } ${constraintValue}`
            : `count-selected(.) <= ${maxChoices}`;
      }

      if (surveyItem.constraint === "") {
         surveyItem.constraint = null;
      }
   });

export const updateSecondQuestionChoices = (survey, questionId, choices) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.secondChoices = choices;
   });

export const updateRangeValues = (survey, questionId, rangeOptions) =>
   editItem(survey, questionId, (surveyItem) => {
      surveyItem.range = rangeOptions;
   });

// export const duplicateSection = (survey, sectionId) => {
//    survey.forEach((item) => {
//       if (item.itemLabel === "Section") {
//          if (item.id !== sectionId) {
//             duplicateSection(item.innerItems, sectionId);
//          } else {
//             const index = survey.findIndex((item) => item.id === sectionId);
//             if (index > -1) {
//                for (let i = index + 1; i < survey.length; i++) {
//                   survey[i].surveyItem.order -= 1;
//                }
//                const id = uuidv4();
//                // const questionItems = {
//                //    ...item.innerItems[index].surveyItem,
//                //    order: item.innerItems[index].surveyItem.order + 1,
//                //    id: id
//                // };

//                survey.splice(index, 0, survey[index]);
//                console.log(survey);
//             }
//          }
//       }
//    });
// };

export const getKeysByValues = (object, values) => {
   return values.map((value) => Object.keys(object).find((key) => object[key] === value));
};
